export default class AnchorCollapses {
  constructor(className = 'denhaag-anchor-collapse') {
    this.panelClassName = className;
    this.contentClassName = `${className}__content`;
    this.summaryClassName = `${className}__summary`;
    this.panels = [];
    this.summaries = [];
    // Above this breakpoint the panels are not collapsible: they are always open and the summary
    // renders as a plain section heading.
    this.desktopQuery = '(min-width: 768px)';
    this.isDesktop = window.matchMedia(this.desktopQuery).matches;
  }

  /**
   * Set up event listeners for custom events to control collapse panels.
   */
  setupEventListeners() {
    window.addEventListener('anchorCollapse:toggle', (e) => {
      const panel = this.getPanel(e.detail.id);
      if (panel) {
        panel.open = !panel.open;
      }
    });

    window.addEventListener('anchorCollapse:open', (e) => this.openCollapse(e.detail.id));
    window.addEventListener('anchorCollapse:close', (e) => this.closeCollapse(e.detail.id));
    window.addEventListener('anchorCollapse:openAll', () => this.forceOpenAll());
    window.addEventListener('anchorCollapse:closeAll', () => this.forceCloseAll());
  }

  /**
   * Initialize the AnchorCollapses by selecting panels and setting up event listeners.
   */
  initialize() {
    this.updateBreakpoint();
    this.panels = [...document.querySelectorAll(`.${this.panelClassName}`)];

    this.panels.forEach((panel, index) => {
      // Listen to native toggle event.
      panel.addEventListener('toggle', () => this.handleToggle(panel));

      const summary = panel.querySelector(`.${this.summaryClassName}`);
      if (summary) {
        this.summaries.push(summary);
        summary.addEventListener('keydown', (event) => this.handleKeydown(event, index));
      }

      // Handle Escape key in content
      panel
        .querySelector(`.${this.contentClassName}`)
        ?.addEventListener('keydown', (event) => this.handleContentKeydown(event, panel));
    });

    this.openOrCloseCollapses();
  }

  /**
   * Refresh the cached breakpoint state.
   *
   * Called on initialize() and from resize(), which the template binds to the theme's debounced
   * `dh:resize` event, so this component needs no resize/orientationchange listeners of its own.
   *
   * @return {boolean} Whether the viewport is at or above the desktop breakpoint.
   */
  updateBreakpoint() {
    this.isDesktop = window.matchMedia(this.desktopQuery).matches;

    return this.isDesktop;
  }

  /**
   * Keep a panel in sync with the native toggle event.
   *
   * Above the desktop breakpoint the panels are presentational and always open, so anything that
   * manages to close one - a stray Escape, a programmatic close - is reverted here. Closing a panel
   * on desktop makes its content inert and invisible until the next resize (DHNL-168).
   *
   * @param {HTMLElement} panel The panel that toggled.
   */
  handleToggle(panel) {
    if (this.isDesktop && !panel.open) {
      // Re-opening fires `toggle` again, which then falls through to updatePanelState().
      panel.open = true;

      return;
    }

    this.updatePanelState(panel);
  }

  /**
   * Close the panel when Escape is pressed inside its content.
   *
   * Only below the desktop breakpoint, where the panel is an actual collapse.
   *
   * @param {object} event The keyboard event object.
   * @param {HTMLElement} panel The panel the content belongs to.
   */
  handleContentKeydown(event, panel) {
    if (this.isDesktop || 'Escape' !== event.key || event.defaultPrevented) {
      return;
    }

    // Leave Escape to nested widgets that need it themselves (dialogs, nested collapses).
    const nestedWidget = event.target?.closest?.('dialog, details, [role="dialog"], [aria-modal="true"]');
    if (nestedWidget && nestedWidget !== panel) {
      return;
    }

    event.preventDefault();
    panel.open = false;
    panel.querySelector(`.${this.summaryClassName}`)?.focus();
  }

  /**
   * Handle keyboard navigation within the anchor collapses.
   * Only active when viewport is below 768px.
   *
   * @param {object} event The keyboard event object.
   * @param {number} index The index of the current panel.
   */
  handleKeydown(event, index) {
    if (this.isDesktop) {
      return;
    }

    let targetIndex;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        targetIndex = (index + 1) % this.summaries.length;
        break;
      case 'ArrowUp':
        event.preventDefault();
        targetIndex = (index - 1 + this.summaries.length) % this.summaries.length;
        break;
      case 'Home':
        event.preventDefault();
        targetIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        targetIndex = this.summaries.length - 1;
        break;
      case 'ArrowLeft':
      case 'Escape':
        this.closeCollapse(this.panels[index].id);
        return;
      case 'ArrowRight':
        this.openCollapse(this.panels[index].id);
        return;
      default:
        return;
    }

    this.summaries[targetIndex]?.focus();
  }

  /**
   * Get a panel by its ID.
   * @param {string} id The ID of the panel.
   * @return {*}
   */
  getPanel(id) {
    return this.panels.find((panel) => panel.id === id);
  }

  /**
   * Update the state of a panel based on its open attribute.
   *
   * @param {HTMLElement} panel The panel to update.
   */
  updatePanelState(panel) {
    if (!panel) return;

    const isOpen = panel.open || false;

    const content = panel.querySelector(`.${this.contentClassName}`);
    if (content) {
      content.inert = !isOpen;
    }

    const summary = panel.querySelector(`.${this.summaryClassName}`);
    if (summary) {
      // On desktop the summary is not interactive, but it holds the section heading. Take it out of
      // the tab order instead of making it inert, which would also hide that heading from
      // assistive technology.
      if (this.isDesktop) {
        summary.tabIndex = -1;
      } else {
        summary.removeAttribute('tabindex');
      }
    }
  }

  /**
   * Handle resize event to open or close collapses based on viewport size.
   */
  resize() {
    this.updateBreakpoint();

    if (!this.panels.length) {
      return;
    }

    this.openOrCloseCollapses();
  }

  /**
   * Open or close collapse panels based on viewport size.
   */
  openOrCloseCollapses() {
    if (!this.panels.length) {
      return;
    }

    if (this.isDesktop) {
      this.forceOpenAll();
    } else {
      this.forceCloseAll();
    }

    const { hash } = window.location;
    if (hash) {
      const cleanedHash = hash.replace('#', '');
      const targetPanel =
        this.getPanel(cleanedHash) || document.getElementById(cleanedHash)?.closest(`.${this.panelClassName}`);

      if (targetPanel) {
        this.openCollapse(targetPanel.id);
        setTimeout(() => targetPanel.scrollIntoView({ behavior: 'smooth' }), 300);
      }
    }
  }

  /**
   * Force open all collapse panels.
   */
  forceOpenAll() {
    if (!this.panels.length) {
      // Do nothing if there are no panels.
      return;
    }

    this.panels?.forEach((panel) => {
      panel.open = true;
      this.updatePanelState(panel);
    });
  }

  /**
   * Force close all collapse panels.
   */
  forceCloseAll() {
    if (!this.panels.length) {
      // Do nothing if there are no panels.
      return;
    }

    this.panels?.forEach((panel) => {
      panel.open = false;
      this.updatePanelState(panel);
    });
  }

  /**
   * Open a specific collapse panel by its ID.
   * @param {string} id The ID of the collapse panel to open.
   */
  openCollapse(id) {
    const panel = this.getPanel(id);
    if (panel && !panel.open) {
      panel.open = true;
    }
  }

  /**
   * Close a specific collapse panel by its ID.
   * @param {string} id The ID of the collapse panel to close.
   */
  closeCollapse(id) {
    const panel = this.getPanel(id);
    if (panel && panel.open) {
      panel.open = false;
    }
  }
}
